/**
 * Pure Plotly trace + layout builders for the Term Structure Decomposition
 * tab. Kept separate from `DecompositionTab.tsx` so the component reads as
 * plain composition (fetch → build → render) and these data-shaping
 * functions can be tested directly.
 */
import type { Data, Layout } from 'plotly.js';
import { CONTRIBUTOR_COLORS, CONTRIBUTOR_FALLBACK_COLORS, hexToRgba } from '@/lib/plotly/colors';
import { hoverTemplate } from '@/lib/plotly/hoverTemplates';
import { AXIS_LINE, mergeLayout } from '@/lib/plotly/defaults';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import { DECOMPOSITION_SHORT_NAMES as SHORT_NAMES } from './decompositionConstants';
import type { DecompositionAsset, DecompositionPayload } from '../types';

/** Asset-class colours for the LHS term-structure lines. */
export const ASSET_COLORS_DECOMP: Record<DecompositionAsset, string> = {
  equities: '#ec665c',
  bonds: '#729dcd',
  commodities: '#ff9d4a',
  inflation: '#9467bd',
};

/** Plotly axis treatment shared by both charts on this tab. */
const AXIS = {
  ...AXIS_LINE,
  ticks: 'inside' as const,
  ticklen: 8,
  tickwidth: 1,
  tickcolor: '#000000',
};

/**
 * LHS chart traces: term-structure lines for the old and new snapshot dates.
 *
 * Uses the same asset colour at two opacities (dashed = old, solid = new).
 */
export function buildTermStructureTraces(payload: DecompositionPayload, color: string): Data[] {
  const { horizons, term_structure: ts } = payload;
  if (!ts || !Array.isArray(horizons)) return [];
  return [
    {
      type: 'scatter' as const,
      mode: 'lines+markers' as const,
      name: payload.old_date_display,
      x: horizons,
      y: ts.old,
      line: { color: hexToRgba(color, 0.5), width: 3, dash: 'dash' as const },
      marker: { color: hexToRgba(color, 0.5), size: 8, symbol: 'circle' },
      hovertemplate: hoverTemplate({ name: payload.old_date_display }),
    },
    {
      type: 'scatter' as const,
      mode: 'lines+markers' as const,
      name: payload.new_date_display,
      x: horizons,
      y: ts.new,
      line: { color, width: 3 },
      marker: { color, size: 8, symbol: 'circle' },
      hovertemplate: hoverTemplate({ name: payload.new_date_display }),
    },
  ];
}

/** Only emit a hover label when the bar value is non-negligible. */
function barHover(value: number, name: string): string {
  return Math.abs(value) > 0.0001 ? hoverTemplate({ name, format: '.3f' }) : '';
}

/**
 * RHS chart traces: stacked-bar decomposition + total-difference line.
 *
 * For each horizon the backend returns the top-3 contributors and an "Other"
 * remainder. We collect all unique contributor names across horizons, then
 * build one bar trace per contributor (zero-filled where absent) plus a
 * scatter trace for the total difference.
 */
export function buildDecompositionTraces(payload: DecompositionPayload): Data[] {
  const { horizons, decomposition: decomp } = payload;
  if (!decomp || !Array.isArray(decomp.contributions)) return [];

  const xLabels = horizons.map((h) => `${h}M`);

  // Collect every unique contributor name across all horizons.
  const uniqueContributors = [
    ...new Set(decomp.contributions.flatMap((bucket) => bucket.top3.map((c) => c.name))),
  ];

  const traces: Data[] = [];

  // One bar trace per contributor (zero-filled where a horizon lacks it).
  uniqueContributors.forEach((name, idx) => {
    const yValues = decomp.contributions.map(
      (bucket) => bucket.top3.find((c) => c.name === name)?.value ?? 0,
    );
    const color =
      CONTRIBUTOR_COLORS[name] ??
      CONTRIBUTOR_FALLBACK_COLORS[idx % CONTRIBUTOR_FALLBACK_COLORS.length];

    traces.push({
      type: 'bar',
      x: xLabels,
      y: yValues,
      name: SHORT_NAMES[name] ?? name,
      marker: { color },
      hovertemplate: yValues.map((v) => barHover(v, name)),
      showlegend: true,
      // Carry the canonical contributor name on every bar so the click
      // handler can resolve the trace regardless of legend short-name.
      meta: { contributor: name },
    } as Data);
  });

  // "Other" bar trace.
  const otherValues = decomp.contributions.map((b) => b.other);
  traces.push({
    type: 'bar',
    x: xLabels,
    y: otherValues,
    name: 'Other',
    marker: { color: CONTRIBUTOR_COLORS.Other },
    hovertemplate: otherValues.map((v) => barHover(v, 'Other')),
    showlegend: true,
    meta: { contributor: 'Other' },
  } as Data);

  // Total-difference line overlay.
  traces.push({
    type: 'scatter',
    mode: 'lines+markers' as const,
    x: xLabels,
    y: decomp.total_difference,
    name: SHORT_NAMES['Total Change'] ?? 'Total Chg',
    line: { color: '#193947', width: 3 },
    marker: { size: 10, symbol: 'diamond', color: '#193947' },
    hovertemplate: hoverTemplate({ name: 'Total Change', format: '.3f' }),
    showlegend: true,
  } as Data);

  return traces;
}

/** LHS layout: term-structure comparison chart. */
export function buildTermStructureLayout(
  payload: DecompositionPayload | undefined,
): Partial<Layout> {
  // Legacy `term-structure-decomposition.js` renders this chart's title at 18
  // and legend at 11; the shared preset's legend now defaults to 16, so pin
  // the legend back to 11 (the title 18 already matches).
  const preset = chartLayout({
    family: 'timeseries',
    title: payload ? `${payload.asset_display} Term Structure` : 'Term Structure Comparison',
  });
  return mergeLayout({
    ...preset,
    legend: {
      ...(preset.legend as object),
      font: { ...((preset.legend as { font?: object })?.font ?? {}), size: 11 },
    },
    xaxis: {
      title: { text: 'Horizon (Months)', standoff: 10 },
      showgrid: true,
      gridcolor: '#E5E7EB',
      ...AXIS,
    },
    yaxis: {
      title: { text: '% Ann.', standoff: 10 },
      showgrid: true,
      gridcolor: '#E5E7EB',
      ...AXIS,
    },
    hovermode: 'x unified' as const,
    autosize: true,
  });
}

/** RHS layout: stacked-bar decomposition chart, with a zero-line shape. */
export function buildDecompositionLayout(
  payload: DecompositionPayload | undefined,
): Partial<Layout> {
  // Legacy `term-structure-decomposition.js` renders this chart's title at 16
  // and legend at 9 (smaller — the contributor legend has many entries); the
  // shared preset now defaults to 18 / 16, so pin both back.
  const preset = chartLayout({
    family: 'bar',
    title: payload
      ? `Revision: ${payload.old_date_display} → ${payload.new_date_display}`
      : 'Decomposition of Change',
  });
  return mergeLayout({
    ...preset,
    title: { ...(preset.title as object), font: { size: 16 } },
    legend: {
      ...(preset.legend as object),
      font: { ...((preset.legend as { font?: object })?.font ?? {}), size: 9 },
    },
    xaxis: {
      title: { text: 'Horizon', standoff: 10 },
      showgrid: false,
      ...AXIS,
    },
    yaxis: {
      title: { text: 'Contribution (% Ann.)', standoff: 10 },
      showgrid: true,
      gridcolor: '#E5E7EB',
      ...AXIS,
    },
    hovermode: 'x unified' as const,
    autosize: true,
    shapes: [
      {
        type: 'line',
        x0: 0,
        x1: 1,
        xref: 'paper',
        y0: 0,
        y1: 0,
        line: { color: '#193947', width: 1 },
      },
    ],
  });
}
